import { LoginStatus, useFacebook } from 'react-facebook';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import { Text1Bold, Text2 } from 'components/typography/Texts';
import { useSocialMediaLogin } from 'features/account/social-media/useSocialMediaLogin';
import { useGlobalError } from 'hooks/useGlobalError';
import { usePopup } from 'hooks/usePopup';

const Container = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${theme.color.borderGray};
  padding: 10px 0;
`;

const Label = styled(Text2)`
  width: 120px;

  ${theme.mq.tablet} {
    width: 80px;
  }
`;

const ConnectText = styled(Text1Bold)`
  color: ${theme.color.blue};
`;

const ValueText = styled(Text2)`
  flex: 1;
  margin: 0 24px;
`;

interface UserDataInputProps {
  label: string;
  value: string;
  refreshCurrentUser: () => void;
  facebookVerified: boolean;
}

export const FacebookInput = ({ label, value, refreshCurrentUser, facebookVerified }: UserDataInputProps) => {
  const { t } = useTranslation('myProfile');
  const { hidePopup } = usePopup();
  const { loginWithFacebook } = useSocialMediaLogin(hidePopup);
  const { api } = useFacebook();
  const { unknownError } = useGlobalError();

  const handleLogin = async () => {
    try {
      const currentStatus = await api!.getLoginStatus();
      if (currentStatus.status === LoginStatus.CONNECTED) {
        await api!.logout();
      }

      const loginResponse = await api!.login({ scope: 'email,public_profile' });
      if (loginResponse.status === LoginStatus.CONNECTED) {
        await loginWithFacebook(loginResponse.authResponse.accessToken);
      }
      refreshCurrentUser();
    } catch (error) {
      unknownError();
    }
  };

  return (
    <Container data-testid={'user-data-facebook'}>
      <Label>{label}</Label>
      <ValueText data-testid={'facebook-value'}>{value}</ValueText>
      {!facebookVerified && (
        <ConnectText data-testid={'connect-facebook'} onClick={() => handleLogin()}>
          {t('account-settings.fb-connect')}
        </ConnectText>
      )}
    </Container>
  );
};
